import { config } from "../../core/config/config.service.es.js";
import Axios from "axios";
let currentPosition = null;
const predictionsCache = new Object(null);
let placeService;
let autocompleteService;
let googleLocal;
let google;
const useGoogleAPI = () => ({
  askForLocation,
  getPlacePredictions,
  getPlaceAddress,
  parseCityFromComponents,
  parseStreetFromComponents,
  parseAddressComponents,
  validateAddress
});
const initGoogleAPI = async () => {
  await includeGoogleAPIScript();
};
const getGoogleAPIKey = () => {
  if (config.googleMapsAPIKey) {
    return config.googleMapsAPIKey;
  }
  const fallbackApiKey = tryFallbackApiKey();
  if (fallbackApiKey) {
    return fallbackApiKey;
  }
  return null;
};
const tryFallbackApiKey = () => {
  const scripts = document.getElementsByTagName("script");
  const regex = /https:\/\/maps\.googleapis\.com\/maps\/api\/js\?key=([^&]+)/i;
  for (const script of scripts) {
    const src = script.getAttribute("src");
    if (src) {
      const match = src.match(regex);
      if (match) {
        return match[1];
      }
    }
  }
  return null;
};
const includeGoogleAPIScript = async () => {
  return new Promise((resolve, reject) => {
    var _a, _b;
    const googleAPIKey = getGoogleAPIKey();
    if ((_b = (_a = window.google) == null ? void 0 : _a.maps) == null ? void 0 : _b.places) {
      resolve(true);
      return;
    }
    if (!googleAPIKey) {
      reject(new Error("Google Maps API key is not set, please update your config"));
      return;
    }
    const lang = document.documentElement.getAttribute("lang") || "en";
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleAPIKey}&libraries=places&language=${lang}&callback=Function.prototype&ver=1.0.0`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      reject(new Error("Failed to load Google Maps script"));
    };
    document.head.appendChild(script);
    setWaitTimeout(100);
  });
};
const setupGoogleAPI = async (newGoogle) => {
  google = newGoogle;
  googleLocal = typeof google === "undefined" ? null : google;
  if (!googleLocal) {
    setWaitTimeout(100);
  }
  autocompleteService = googleLocal && googleLocal.maps ? new googleLocal.maps.places.AutocompleteService(
    document.createElement("input")
  ) : {};
  if (googleLocal && googleLocal.maps) {
    const { Place } = await google.maps.importLibrary("places");
    placeService = Place;
  } else {
    placeService = {};
  }
};
function askForLocation() {
  if (!currentPosition && navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        currentPosition = position.coords;
      },
      () => {
      },
      {
        enableHighAccuracy: false,
        maximumAge: 5e3
      }
    );
  }
}
function getPlacePredictions(input, types = "(cities)") {
  if (!googleLocal) {
    return Promise.reject(new Error("google not loaded"));
  }
  if (!input) {
    return Promise.resolve([]);
  }
  const hash = `${input}::${types}`;
  if (predictionsCache[hash]) {
    return Promise.resolve(predictionsCache[hash]);
  }
  return new Promise((resolve, reject) => {
    autocompleteService.getPlacePredictions(
      {
        input,
        types: [types],
        // specifying location and radius did not enhance results, for example for
        // Sedmerovec 93 address.. it did not find it neither
        // therefore I am not going to make code more complex, without any value
        ...currentPosition ? {
          radius: 5e3,
          location: new google.maps.LatLng(
            currentPosition.latitude,
            currentPosition.longitude
          )
        } : {
          // fallbacks to whole europe
          bounds: config.bounds || {
            east: -24.550781,
            south: 34.741612,
            west: 40.605468,
            north: 71.244356
          }
        }
      },
      (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK || status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          predictionsCache[hash] = results;
          resolve(results ?? []);
        } else {
          reject({ results, status });
        }
      }
    );
  });
}
function getPlaceAddress(placeId) {
  return new Promise(async (resolve, reject) => {
    const place = new placeService({ id: placeId });
    try {
      await place.fetchFields(
        {
          fields: ["addressComponents", "postalAddress"]
        }
      );
      const parsedAddressComponents = parseAddressComponents(place.addressComponents);
      parsedAddressComponents.street_formatted = "";
      if (place.postalAddress && place.postalAddress.addressLines) {
        parsedAddressComponents.street_formatted = place.postalAddress.addressLines[0] || "";
      }
      resolve(parsedAddressComponents);
    } catch (e) {
      console.log("GoogleAPIService: Error fetching place address:", e);
      reject(e);
    }
  });
}
function parseStreetFromComponents(components) {
  const route = components.route;
  const streetNumber = components.street_number;
  const premise = components.premise;
  let houseNumber = "";
  if (streetNumber && premise) {
    houseNumber = `${premise}/${streetNumber}`;
  } else if (streetNumber) {
    houseNumber = streetNumber;
  } else if (premise) {
    houseNumber = premise;
  }
  return [route, houseNumber].join(" ").trim();
}
function parseCityFromComponents(components) {
  return components.postal_town || components.locality || components.sublocality || components.sublocality_level_5 || components.sublocality_level_4 || components.sublocality_level_3 || components.sublocality_level_2 || components.sublocality_level_1 || components.administrative_area_level_5 || components.administrative_area_level_4 || components.administrative_area_level_3 || components.administrative_area_level_2 || components.administrative_area_level_1 || "";
}
function parseAddressComponents(address_components = []) {
  const parsed_components = {};
  const componentsLength = address_components.length;
  for (let i = 0; i < componentsLength; ++i) {
    const component = address_components[i];
    const types = component.types;
    for (let ii = 0; ii < types.length; ++ii) {
      const type = types[ii];
      parsed_components[type] = component.longText;
      if (type === "country") {
        parsed_components.country_code = component.shortText;
      }
    }
  }
  return parsed_components;
}
async function validateAddress(validationRequest) {
  const comparativeResults = {
    provided: {
      street: validationRequest.address.addressLines[0],
      city: validationRequest.address.addressLines[1],
      postalCode: validationRequest.address.postalCode
    },
    suggested: {
      street: "",
      city: "",
      postalCode: ""
    },
    haveSuggestions: false
  };
  const validationResult = {
    approved: false,
    errors: [],
    suggestions: comparativeResults
  };
  const googleAPIKey = getGoogleAPIKey();
  if (!googleAPIKey) {
    console.log("GoogleAPIService: Google Maps API key is not set, please update your config");
    return;
  }
  const result = await Axios.post(`https://addressvalidation.googleapis.com/v1:validateAddress?key=${googleAPIKey}`, validationRequest);
  const { verdict, address } = result.data.result;
  const { addressComplete, possibleNextAction, inputGranularity, validationGranularity } = verdict;
  const { addressComponents, postalAddress } = address;
  const { addressLines, locality, postalCode, administrativeArea } = postalAddress;
  if (validationGranularity === "OTHER") {
    validationResult.errors.push("Address granularity is OTHER, expected PREMISE, SUB_PREMISE or ROUTE.");
  }
  if (inputGranularity === "OTHER") {
    validationResult.errors.push("Address input granularity is OTHER, expected PREMISE, SUB_PREMISE or ROUTE.");
  }
  if (typeof addressComplete !== "undefined" && !addressComplete) {
    validationResult.errors.push("Address is not complete.");
  }
  if (possibleNextAction === "FIX" || possibleNextAction === "CONFIRM_ADD_SUBPREMISES") {
    validationResult.errors.push("Address is not valid.");
  }
  addressComponents.forEach((addressComponent) => {
    if (addressComponent.inferred) {
      if (addressComponent.componentType !== "street_number" && addressComponent.componentType !== "premise" && addressComponent.componentType !== "country") {
        return;
      }
      validationResult.errors.push(
        `Address component ${addressComponent.componentType} is inferred`
      );
      return;
    }
    const hasToBeConfirmedTypes = [
      "route",
      "locality",
      "postal_code",
      "country",
      "sublocality_level_1",
      "postal_town"
    ];
    if (addressComponent.confirmationLevel !== "CONFIRMED" && hasToBeConfirmedTypes.includes(addressComponent.componentType)) {
      validationResult.errors.push(
        `Address component ${addressComponent.componentType} is not confirmed.`
      );
    }
  });
  if (addressLines && addressLines.length > 0) {
    comparativeResults.suggested.street = addressLines[0];
  }
  if (locality) {
    comparativeResults.suggested.city = locality;
    if (administrativeArea) {
      comparativeResults.suggested.city += `, ${administrativeArea}`;
    }
  }
  if (postalCode) {
    comparativeResults.suggested.postalCode = postalCode;
  }
  if (comparativeResults.suggested.city !== comparativeResults.provided.city || comparativeResults.suggested.street !== comparativeResults.provided.street || comparativeResults.suggested.postalCode !== comparativeResults.provided.postalCode) {
    validationResult.suggestions.haveSuggestions = true;
  }
  validationResult.suggestions = comparativeResults;
  if (validationResult.errors.length === 0) {
    validationResult.approved = true;
  }
  return validationResult;
}
function setWaitTimeout(time) {
  setTimeout(async () => {
    var _a;
    if (time > 3e4) {
      return;
    }
    googleLocal = typeof window.google === "undefined" ? null : window.google;
    if (!((_a = googleLocal == null ? void 0 : googleLocal.maps) == null ? void 0 : _a.places)) {
      setWaitTimeout(time *= 2);
    } else {
      google = window.google;
      googleLocal = google;
      autocompleteService = new googleLocal.maps.places.AutocompleteService();
      if (googleLocal && googleLocal.maps) {
        const { Place } = await google.maps.importLibrary("places");
        placeService = Place;
      } else {
        placeService = {};
      }
    }
  }, time);
}
export {
  includeGoogleAPIScript,
  initGoogleAPI,
  setupGoogleAPI,
  useGoogleAPI
};
//# sourceMappingURL=googleAPI.service.es.js.map
